import { z } from 'zod';

export const INS_STAGES = ['TO_SCHEDULE', 'SCHEDULED', 'IN_PROGRESS', 'SNAGS', 'COMPLETED', 'CANCELLED'] as const;
export type InsStage = (typeof INS_STAGES)[number];
export const INS_STAGE_LABELS: Record<InsStage, string> = { TO_SCHEDULE: 'To schedule', SCHEDULED: 'Scheduled', IN_PROGRESS: 'Installing', SNAGS: 'Snags open', COMPLETED: 'Handed over', CANCELLED: 'Cancelled' };
export const INS_ROLES = { LEAD: 'Team lead', FIXER: 'Fixer', HELPER: 'Helper', PAINTER: 'Painter', ELECTRICIAN: 'Electrician' } as const;
export type InsRole = keyof typeof INS_ROLES;
export type InsVisitStatus = 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';

const uuid = z.string().uuid();
const isoTs = z.string().min(16);

export const insOrderSchema = z.object({
  salesOrderId: uuid,
  siteAddress: z.string().trim().max(500).default(''),
  contactName: z.string().trim().max(120).default(''),
  contactPhone: z.string().trim().max(40).default(''),
  preferredDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  notes: z.string().trim().max(1000).default(''),
  lines: z.array(z.object({ soLineId: uuid.nullable().default(null), itemId: uuid.nullable().default(null), description: z.string().trim().max(300).default(''), qty: z.coerce.number().positive() })).min(1).max(100),
});
export type InsOrderInput = z.infer<typeof insOrderSchema>;

export const insVisitSchema = z
  .object({
    kind: z.enum(['INSTALL', 'SNAG']).default('INSTALL'),
    plannedStart: isoTs,
    plannedEnd: isoTs,
    notes: z.string().trim().max(500).default(''),
    crew: z.array(z.object({ employeeId: uuid, role: z.enum(['LEAD', 'FIXER', 'HELPER', 'PAINTER', 'ELECTRICIAN']).default('FIXER') })).min(1, 'Add at least one person').max(20),
  })
  .refine((v) => v.plannedEnd > v.plannedStart, { path: ['plannedEnd'], message: 'Must be after the start' });
export type InsVisitInput = z.infer<typeof insVisitSchema>;

export const insSnagSchema = z.object({ description: z.string().trim().min(3).max(500), severity: z.enum(['MINOR', 'MAJOR']).default('MINOR') });
export type InsSnagInput = z.infer<typeof insSnagSchema>;

export const insFinishSchema = z.object({
  report: z.string().trim().max(1000).default(''),
  installed: z.array(z.object({ lineId: uuid, qty: z.coerce.number().min(0) })).max(100).default([]),
  snags: z.array(insSnagSchema).max(30).default([]),
  fixedSnagIds: z.array(uuid).max(50).default([]),
});
export type InsFinishInput = z.infer<typeof insFinishSchema>;

export const INS_CHECKLIST = ['All items installed as per the drawing', 'Site cleaned, packing removed', 'Doors, drawers and mechanisms work', 'No visible damage or scratches', 'Care and warranty explained'] as const;
export const insHandoverSchema = z.object({
  signedBy: z.string().trim().min(2, 'Name of the person signing').max(120),
  signature: z.string().startsWith('data:image/png;base64,', 'Sign in the box').max(400_000),
  checklist: z.array(z.object({ item: z.string().max(200), ok: z.boolean() })).max(20),
  rating: z.number().int().min(1).max(5).nullable().default(null),
  comments: z.string().trim().max(1000).default(''),
});
export type InsHandoverInput = z.infer<typeof insHandoverSchema>;

export interface InsCrewItem { employeeId: string; name: string; role: InsRole }
export interface InsVisitItem { id: string; seq: number; kind: 'INSTALL' | 'SNAG'; status: InsVisitStatus; plannedStart: string; plannedEnd: string; startedAt: string | null; finishedAt: string | null; notes: string; report: string; crew: InsCrewItem[] }
export interface InsSnagItem { id: string; no: number; description: string; severity: 'MINOR' | 'MAJOR'; status: 'OPEN' | 'FIXED'; raisedAt: string; fixedAt: string | null; fixNote: string }
export interface InsOrderDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  stage: InsStage;
  docDate: string;
  salesOrder: { id: string; number: string | null; customer: string | null; committedDate: string | null };
  /** A service visit for an after-sales ticket. */
  serviceTicket: { id: string; number: string | null } | null;
  delivery: { id: string; number: string | null; docDate: string } | null;
  siteAddress: string;
  contactName: string;
  contactPhone: string;
  preferredDate: string | null;
  dueDate: string | null;
  notes: string;
  startedAt: string | null;
  completedAt: string | null;
  lines: { id: string; item: { id: string; code: string; name: string } | null; description: string; qty: number; installed: number }[];
  visits: InsVisitItem[];
  snags: InsSnagItem[];
  handover: { signedBy: string; signature: string; checklist: { item: string; ok: boolean }[]; rating: number | null; comments: string; signedAt: string; recordedBy: string } | null;
  version: number;
  actions: import('./documents').WorkflowAction[];
}
export interface InsOrderListItem { id: string; number: string | null; stage: InsStage; customer: string | null; salesOrder: string | null; siteAddress: string; items: string; nextVisit: string | null; crew: string[]; openSnags: number; service: boolean; dueDate: string | null; overdue: boolean; docDate: string }
export interface InsBoardData {
  days: string[];
  people: { id: string; name: string; department: string; off: Record<string, 'holiday' | 'leave' | 'weekly_off'>; busy: { start: string; end: string; label: string }[] }[];
  visits: { id: string; orderId: string; number: string | null; customer: string | null; siteAddress: string; kind: 'INSTALL' | 'SNAG'; status: InsVisitStatus; plannedStart: string; plannedEnd: string; crew: InsCrewItem[] }[];
  unscheduled: { id: string; number: string | null; customer: string | null; siteAddress: string; items: string; dueDate: string | null; overdue: boolean }[];
}
export interface InsCrewSuggestion { lead: { id: string; name: string } | null; members: { id: string; name: string; role: InsRole }[]; warnings: string[] }

export interface InsSnagRow { id: string; no: number; orderId: string; number: string | null; customer: string | null; siteAddress: string; description: string; severity: 'MINOR' | 'MAJOR'; status: 'OPEN' | 'FIXED'; raisedAt: string; ageDays: number; nextVisit: string | null }
export interface InsReport {
  from: string;
  to: string;
  summary: { completed: number; avgDaysToHandover: number | null; firstTimeRightPct: number | null; avgRating: number | null; snagsRaised: number; visitsDone: number; hoursOnSite: number };
  installers: { name: string; visits: number; hoursOnSite: number; asLead: number; snagsOnTheirWork: number }[];
  capacity: { day: string; installers: number; availableHours: number; bookedHours: number }[];
  completed: { id: string; number: string | null; customer: string | null; deliveredOn: string | null; handedOverOn: string; days: number | null; snags: number; rating: number | null }[];
}
